import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap, Shield, Wallet, ChevronRight, Wifi, WifiOff } from 'lucide-react';
import { useNetwork } from '../../hooks/useNetwork';
import logo from '../../utils/marketlite-logo.png';

const slides = [
  {
    id: 0,
    icon: null, // uses logo
    useLogo: true,
    title: 'Welcome to\nMarketLite',
    subtitle: 'The fastest way to buy prepaid electricity tokens for your shop — anytime, anywhere.',
    bg: 'from-teal-600 via-teal-700 to-indigo-800',
    accent: 'bg-amber-400',
    dotColor: 'bg-amber-400',
  },
  {
    id: 1,
    icon: Zap,
    useLogo: false,
    title: 'Tokens in\nSeconds',
    subtitle: 'No queues, no agents — purchase electricity units instantly and enter them directly into your meter.',
    bg: 'from-indigo-700 via-indigo-800 to-slate-900',
    accent: 'bg-amber-400',
    dotColor: 'bg-amber-400',
    iconBg: 'bg-amber-400/20',
    iconColor: 'text-amber-400',
  },
  {
    id: 2,
    icon: Shield,
    useLogo: false,
    title: 'Safe &\nSecure',
    subtitle: 'Every payment is encrypted end-to-end. Your wallet, transactions, and personal data are always protected.',
    bg: 'from-emerald-700 via-teal-800 to-indigo-900',
    accent: 'bg-emerald-400',
    dotColor: 'bg-emerald-400',
    iconBg: 'bg-emerald-400/20',
    iconColor: 'text-emerald-400',
  },
  {
    id: 3,
    icon: Wallet,
    useLogo: false,
    title: 'All Your\nMeters',
    subtitle: 'Manage multiple shop meters from one wallet. Track spending, view history, and get low-balance alerts.',
    bg: 'from-slate-800 via-slate-900 to-indigo-950',
    accent: 'bg-teal-400',
    dotColor: 'bg-teal-400',
    iconBg: 'bg-teal-400/20',
    iconColor: 'text-teal-400',
  },
];

const AppLanding = ({ onDone }) => {
  const navigate = useNavigate();
  const { isOnline } = useNetwork();
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [fadeIn, setFadeIn] = useState(true);

  // Auto-advance slides
  useEffect(() => {
    if (current >= slides.length - 1) return;
    const t = setTimeout(() => goTo(current + 1), 4500);
    return () => clearTimeout(t);
  }, [current]);

  const goTo = (idx) => {
    if (animating) return;
    setAnimating(true);
    setFadeIn(false);
    setTimeout(() => {
      setCurrent(idx);
      setFadeIn(true);
      setAnimating(false);
    }, 280);
  };

  const handleGetStarted = () => {
    if (onDone) onDone();
    navigate('/login', { state: { isLogin: false } });
  };

  const handleLogin = () => {
    if (onDone) onDone();
    navigate('/login', { state: { isLogin: true } });
  };

  const slide = slides[current];
  const isLast = current === slides.length - 1;

  return (
    <div
      className={`fixed inset-0 flex flex-col bg-gradient-to-br ${slide.bg} transition-all duration-700 select-none overflow-hidden`}
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      {/* Offline indicator */}
      {!isOnline && (
        <div className="absolute top-0 left-0 right-0 z-50 flex items-center justify-center gap-1.5 py-1.5 bg-gray-900/80 text-white text-xs">
          <WifiOff size={12} /> Offline mode – limited functionality
        </div>
      )}

      {/* Skip */}
      {!isLast && (
        <button
          onClick={() => goTo(slides.length - 1)}
          className="absolute top-5 right-5 z-10 text-white/50 text-sm font-medium px-3 py-1.5 rounded-full hover:text-white/80 hover:bg-white/10 transition-all"
          style={{ marginTop: 'env(safe-area-inset-top, 0px)' }}
        >
          Skip
        </button>
      )}

      {/* Decorative blobs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-20 -left-20 w-72 h-72 bg-white/5 rounded-full blur-3xl" />
        <div className="absolute bottom-40 -right-20 w-64 h-64 bg-white/5 rounded-full blur-3xl" />
      </div>

      {/* Main content */}
      <div
        className={`flex-1 flex flex-col items-center justify-center px-8 transition-all duration-300 ${fadeIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
      >
        {/* Icon / Logo */}
        <div className="mb-8">
          {slide.useLogo ? (
            <div className="w-28 h-28 rounded-3xl bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center shadow-2xl shadow-black/30 overflow-hidden">
              <img src={logo} alt="MarketLite" className="w-20 h-20 object-contain" />
            </div>
          ) : (
            <div className={`w-24 h-24 rounded-3xl ${slide.iconBg} border border-white/10 flex items-center justify-center shadow-xl`}>
              <slide.icon size={44} className={slide.iconColor} />
            </div>
          )}
        </div>

        {/* Text */}
        <div className="text-center space-y-4 max-w-xs">
          <h1 className="text-4xl font-extrabold text-white leading-tight whitespace-pre-line tracking-tight">
            {slide.title}
          </h1>
          <p className="text-base text-white/65 leading-relaxed">
            {slide.subtitle}
          </p>
        </div>
      </div>

      {/* Bottom section */}
      <div className="px-8 pb-8 space-y-5">
        {/* Dots */}
        <div className="flex items-center justify-center gap-2">
          {slides.map((s, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className={`rounded-full transition-all duration-300 ${
                i === current
                  ? `w-6 h-2.5 ${slide.dotColor}`
                  : 'w-2.5 h-2.5 bg-white/25'
              }`}
            />
          ))}
        </div>

        {/* CTA buttons */}
        {isLast ? (
          <div className="space-y-3">
            <button
              onClick={handleGetStarted}
              className="w-full py-4 bg-amber-400 text-gray-900 font-bold text-base rounded-2xl shadow-2xl shadow-amber-500/30 active:scale-95 transition-transform flex items-center justify-center gap-2"
            >
              Create Free Account
              <ChevronRight size={18} />
            </button>
            <button
              onClick={handleLogin}
              className="w-full py-3.5 bg-white/10 backdrop-blur-md border border-white/20 text-white font-semibold text-base rounded-2xl active:scale-95 transition-transform"
            >
              I already have an account
            </button>
          </div>
        ) : (
          <button
            onClick={() => goTo(current + 1)}
            className={`w-full py-4 ${slide.accent} text-gray-900 font-bold text-base rounded-2xl shadow-xl active:scale-95 transition-transform flex items-center justify-center gap-2`}
          >
            Next
            <ChevronRight size={18} />
          </button>
        )}
      </div>
    </div>
  );
};

export default AppLanding;
